import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import CommissionSummary from './components/CommissionSummary';
import CommissionBreakdown from './components/CommissionBreakdown';
import PayoutManagement from './components/PayoutManagement';
import CommissionRates from './components/CommissionRates';
import CommissionAnalytics from './components/CommissionAnalytics';
import TransactionCommissions from './components/TransactionCommissions';

const CommissionManagement = () => {
  const navigate = useNavigate();
  const [user] = useState({
    id: 'agent-001',
    name: 'Michael Rodriguez',
    email: 'michael.rodriguez@agent.com',
    role: 'agent'
  });

  const [selectedTimeRange, setSelectedTimeRange] = useState('30d');
  const [selectedTerritory, setSelectedTerritory] = useState('all');
  const [selectedTransactionType, setSelectedTransactionType] = useState('all');

  // Mock commission data
  const commissionData = {
    currentBalance: 15420.50,
    pendingPayouts: 2800.00,
    totalEarnings: 89750.30,
    thisMonthEarnings: 12450.75,
    lastPayoutDate: '2024-11-15',
    nextPayoutDate: '2024-12-01',
    minimumPayout: 500.00,
    commissionRate: 2.5
  };

  const transactions = [
    {
      id: 'TXN-2024-001',
      date: '2024-11-18',
      customerName: 'Sarah Johnson',
      transactionAmount: 5000.00,
      currency: 'USD',
      commissionRate: 2.5,
      commissionAmount: 125.00,
      territory: 'North America',
      type: 'International Transfer',
      status: 'completed'
    },
    {
      id: 'TXN-2024-002',
      date: '2024-11-17',
      customerName: 'David Chen',
      transactionAmount: 2500.00,
      currency: 'USD',
      commissionRate: 3.0,
      commissionAmount: 75.00,
      territory: 'Asia Pacific',
      type: 'Domestic Transfer',
      status: 'completed'
    },
    {
      id: 'TXN-2024-003',
      date: '2024-11-16',
      customerName: 'Maria Garcia',
      transactionAmount: 8000.00,
      currency: 'USD',
      commissionRate: 2.0,
      commissionAmount: 160.00,
      territory: 'Latin America',
      type: 'International Transfer',
      status: 'pending'
    }
  ];

  const rateStructures = [
    {
      territory: 'North America',
      baseRate: 2.5,
      tierRates: [
        { volume: '0-10,000', rate: 2.5 },
        { volume: '10,001-50,000', rate: 2.75 },
        { volume: '50,001+', rate: 3.0 }
      ],
      bonusEligible: true
    },
    {
      territory: 'Europe',
      baseRate: 2.0,
      tierRates: [
        { volume: '0-15,000', rate: 2.0 },
        { volume: '15,001-75,000', rate: 2.25 },
        { volume: '75,001+', rate: 2.5 }
      ],
      bonusEligible: true
    },
    {
      territory: 'Asia Pacific',
      baseRate: 3.0,
      tierRates: [
        { volume: '0-8,000', rate: 3.0 },
        { volume: '8,001-40,000', rate: 3.25 },
        { volume: '40,001+', rate: 3.5 }
      ],
      bonusEligible: false
    }
  ];

  const analyticsData = {
    monthlyTrends: [
      { month: 'Jul', earnings: 8500 },
      { month: 'Aug', earnings: 9200 },
      { month: 'Sep', earnings: 10100 },
      { month: 'Oct', earnings: 11800 },
      { month: 'Nov', earnings: 12450 }
    ],
    territoryBreakdown: [
      { territory: 'North America', percentage: 45, earnings: 5602.50 },
      { territory: 'Europe', percentage: 30, earnings: 3735.00 },
      { territory: 'Asia Pacific', percentage: 25, earnings: 3112.50 }
    ],
    topPerformingTransactionTypes: [
      { type: 'International Transfer', count: 45, earnings: 8250.00 },
      { type: 'Domestic Transfer', count: 32, earnings: 2400.00 },
      { type: 'Business Transfer', count: 18, earnings: 1800.00 }
    ]
  };

  const handleLogout = () => {
    navigate('/login');
  };

  const handleFilterChange = (filterType, value) => {
    switch (filterType) {
      case 'timeRange':
        setSelectedTimeRange(value);
        break;
      case 'territory':
        setSelectedTerritory(value);
        break;
      case 'transactionType':
        setSelectedTransactionType(value);
        break;
      default:
        break;
    }
  };

  const handlePayoutRequest = (amount, paymentMethod) => {
    console.log(`Payout request: $${amount} via ${paymentMethod}`);
    // Handle payout request logic
  };

  const handleDisputeSubmission = (transactionId, reason) => {
    console.log(`Dispute submitted for ${transactionId}: ${reason}`);
    // Handle dispute submission logic
  };

  const handleStatementGeneration = (dateRange) => {
    console.log(`Generating statement for ${dateRange}`);
    // Handle statement generation logic
  };

  return (
    <div className="min-h-screen bg-background">
      <Header user={user} onLogout={handleLogout} />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-foreground">Commission Management</h1>
          <p className="text-muted-foreground mt-2">
            Track earnings, manage payouts, and analyze commission performance
          </p>
        </div>

        {/* Commission Summary */}
        <div className="mb-8">
          <CommissionSummary 
            data={commissionData}
            onFilterChange={handleFilterChange}
            selectedTimeRange={selectedTimeRange}
            selectedTerritory={selectedTerritory}
            selectedTransactionType={selectedTransactionType}
          />
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-8">
          {/* Commission Details - Left Column */}
          <div className="lg:col-span-2 space-y-8">
            {/* Commission Breakdown */}
            <CommissionBreakdown 
              transactions={transactions}
              onDisputeSubmit={handleDisputeSubmission}
            />
            
            {/* Transaction-Level Commissions */}
            <TransactionCommissions 
              transactions={transactions}
              filters={{
                timeRange: selectedTimeRange,
                territory: selectedTerritory,
                transactionType: selectedTransactionType
              }}
            />
          </div>

          {/* Sidebar - Right Column */}
          <div className="lg:col-span-1 space-y-8">
            {/* Payout Management */}
            <PayoutManagement 
              currentBalance={commissionData?.currentBalance}
              minimumPayout={commissionData?.minimumPayout}
              nextPayoutDate={commissionData?.nextPayoutDate}
              onPayoutRequest={handlePayoutRequest}
            />

            {/* Commission Analytics */}
            <CommissionAnalytics 
              data={analyticsData}
            />
          </div>
        </div>

        {/* Commission Rate Tables */}
        <div className="mb-8">
          <CommissionRates 
            rateStructures={rateStructures}
            currentUserTerritory="North America"
            onStatementGenerate={handleStatementGeneration}
          />
        </div>
      </main>
    </div>
  );
};

export default CommissionManagement;